import { styled } from '@mui/material/styles'
import Slider from 'react-slick'
import { formatHour, getChargingPowerTypeDisplay } from './../../../utilities/generalUtils'
import Box from '@mui/material/Box'
import React, { useEffect, useState } from 'react'
import { useData } from '../../../data/DataProvider'
import Skeleton from 'react-loading-skeleton'
import LinearProgress, {
    linearProgressClasses,
} from '@mui/material/LinearProgress'
import { GpCard, GpDivider, Icon } from '../../../components/gp'

const SampleNextArrow = (props) => {
    const { className, style, onClick } = props
    return (
        <div
            className={className}
            style={{ ...style, display: 'block' }}
            onClick={onClick}
        />
    )
}

const SamplePrevArrow = (props) => {
    const { className, style, onClick } = props
    return (
        <div
            className={className}
            style={{ ...style, display: 'block' }}
            onClick={onClick}
        />
    )
}

const BorderLinearProgress = styled(LinearProgress)(() => ({
    height: 8,
    marginTop: 8,
    marginBottom: 8,
    borderRadius: 999,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: 'var(--gp-chart-rail)',
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 999,
        backgroundColor: 'var(--gp-brand)',
    },
}))

const cardMinHeight = { minHeight: 260 }

const MyStatus = (props) => {
    const data = useData()

    const [activeSessions, setActiveSessions] = useState(null)

    const sliderSettings = {
        dots: true,
        infinite: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        nextArrow: <SampleNextArrow />,
        prevArrow: <SamplePrevArrow />,
    }

    useEffect(() => {
        if (!data.userDashboardSessions) {
            return
        }
        if (data.userDashboardSessions.activeSessions) {
            setActiveSessions(data.userDashboardSessions.activeSessions)
        }
    }, [data.userDashboardSessions])

    return (
        <div className="gp-col">
            <div className="dash-section-title">My Status</div>

            {activeSessions === null && (
                <GpCard padding={22} style={cardMinHeight}>
                    <Skeleton height="210px" borderRadius="10px" />
                </GpCard>
            )}

            {activeSessions !== null && !activeSessions?.length && (
                <GpCard padding={22} style={cardMinHeight}>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            padding: '20px 4px',
                            gap: 18,
                            minHeight: 216,
                        }}
                    >
                        <div
                            style={{
                                width: 56,
                                height: 56,
                                borderRadius: '50%',
                                background: 'var(--gp-surface)',
                                border: '1px dashed var(--gp-hairline)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'var(--gp-ink-mute)',
                                flexShrink: 0,
                            }}
                        >
                            <Icon name="plug" size={24} stroke={1.5} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div style={{ font: 'var(--gp-body-med)', color: 'var(--gp-ink)' }}>
                                No active charging sessions
                            </div>
                            <div
                                style={{
                                    font: 'var(--gp-caption)',
                                    color: 'var(--gp-ink-mute)',
                                    maxWidth: 220,
                                }}
                            >
                                Tap into an EV charger to start a new session.
                            </div>
                        </div>
                    </div>
                </GpCard>
            )}

            {activeSessions?.length > 0 && (
                <Slider {...sliderSettings}>
                    {activeSessions.map((activeSession, index) => {
                        const powerDraw = activeSession.session.powerDraw
                        const voltsAvg = powerDraw?.voltsAvg ?? 0
                        const amperesAvg = powerDraw?.amperesAvg ?? 0
                        const chargingSpeed = parseFloat(
                            (voltsAvg * amperesAvg) / 1000
                        ).toFixed(1)

                        const powerConsumedInKwh =
                            activeSession.session.powerConsumedInKwh
                        const cumulativeEnergy = `${powerConsumedInKwh.power}${powerConsumedInKwh.unit}`

                        const chargeType = getChargingPowerTypeDisplay(
                            activeSession.session.chargeType,
                            props.powerType,
                            chargingSpeed
                        )

                        return (
                            <GpCard key={index} padding={22} style={cardMinHeight}>
                                {activeSessions.length > 1 && (
                                    <div
                                        style={{
                                            font: '500 11px/1.3 var(--gp-font)',
                                            letterSpacing: 0.4,
                                            textTransform: 'uppercase',
                                            color: 'var(--gp-ink-mute)',
                                            marginBottom: 8,
                                        }}
                                    >
                                        Active Session {index + 1}
                                    </div>
                                )}
                                <div
                                    style={{
                                        font: '500 28px/1 var(--gp-font)',
                                        letterSpacing: -0.5,
                                        color: 'var(--gp-ink)',
                                    }}
                                >
                                    {cumulativeEnergy}
                                </div>
                                <div
                                    style={{
                                        font: 'var(--gp-body-med)',
                                        color: 'var(--gp-ink)',
                                        marginTop: 4,
                                    }}
                                >
                                    {chargeType}
                                </div>
                                <GpDivider spacing={14} />
                                <div style={{ font: 'var(--gp-body)', color: 'var(--gp-ink)' }}>
                                    {activeSession.session.rangeRequired}{' '}
                                    <span style={{ color: 'var(--gp-ink-mute)' }}>
                                        miles requested
                                    </span>
                                </div>
                                <GpDivider spacing={14} />
                                <div style={{ font: 'var(--gp-body)', color: 'var(--gp-ink)' }}>
                                    <span style={{ color: 'var(--gp-ink-mute)' }}>
                                        Leaving at{' '}
                                    </span>
                                    {formatHour(activeSession.session.departure)}
                                </div>
                                <Box sx={{ flexGrow: 1, mt: 1 }}>
                                    <BorderLinearProgress
                                        variant="determinate"
                                        value={activeSession.session.fulfilled}
                                    />
                                </Box>
                                <div
                                    style={{
                                        font: 'var(--gp-body-med)',
                                        color: 'var(--gp-ink)',
                                        marginTop: 6,
                                    }}
                                >
                                    {activeSession.session.milesCharged}{' '}
                                    <span style={{ color: 'var(--gp-ink-mute)' }}>
                                        miles
                                    </span>
                                    {!props.isRecurring && (
                                        <>
                                            , ${activeSession.session.chargingCost}{' '}
                                            <span style={{ color: 'var(--gp-ink-mute)' }}>
                                                spent
                                            </span>
                                        </>
                                    )}
                                </div>
                            </GpCard>
                        )
                    })}
                </Slider>
            )}
        </div>
    )
}

export default MyStatus
